import { useState, useEffect, Suspense, lazy } from 'react';
import { useCart } from '@/contexts/CartContext';
import Header from '@/components/Header';
import Hero from '@/components/Hero';
import SEO from '@/components/SEO';
import { settingsStore } from '@/services/apiService';

// Mniej wrażliwe sekcje ładowane dynamicznie (lazy) by przyspieszyć wczytywanie Hero
const TrustBadges = lazy(() => import('@/components/TrustBadges'));
const CategoriesSection = lazy(() => import('@/components/CategoriesSection'));
const FeaturedProducts = lazy(() => import('@/components/FeaturedProducts'));
const TestimonialsSection = lazy(() => import('@/components/TestimonialsSection'));
const StatsStrip = lazy(() => import('@/components/StatsStrip'));
const AboutSection = lazy(() => import('@/components/AboutSection'));
const GallerySection = lazy(() => import('@/components/GallerySection'));
const BlogLatestSection = lazy(() => import('@/components/Blog/BlogLatestSection'));
const WaveDivider = lazy(() => import('@/components/WaveDivider'));
const CartDrawer = lazy(() => import('@/components/CartDrawer'));
const Footer = lazy(() => import('@/components/Footer'));

const Index = () => {
  const { isCartOpen, setIsCartOpen } = useCart();

  useEffect(() => {

    // Dynamic preload for critical home page images to avoid console warnings on other pages
    const preloadImages = ['/ui/hero/hero-vineyard.webp', '/ui/age-gate/age-gate-1.webp', '/ui/age-gate/age-gate-2.webp'];
    const links: HTMLLinkElement[] = [];

    preloadImages.forEach(src => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = src;
        // @ts-ignore
        link.fetchPriority = 'high';
        document.head.appendChild(link);
        links.push(link);
    });

    return () => {
        links.forEach(link => {
            if (document.head.contains(link)) {
                document.head.removeChild(link);
            }
        });
    };
  }, []);

  // Smooth scroll handler
  useEffect(() => {
    const handleSmoothScroll = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href^="#"]');

      if (anchor) {
        e.preventDefault();
        const href = anchor.getAttribute('href');
        if (href && href.startsWith('#')) {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }
    };

    document.addEventListener('click', handleSmoothScroll);
    return () => document.removeEventListener('click', handleSmoothScroll);
  }, []);

  const settings = settingsStore.get();
  
  // Próba parsowania adresu (format: "ul. Winiarska 15, 00-001 Warszawa")
  const addressParts = settings.address?.split(',') || [];
  const streetAddress = addressParts[0]?.trim() || '';
  const cityZipParts = addressParts[1]?.trim().split(' ') || [];
  const postalCode = cityZipParts.length >= 2 ? cityZipParts[0] : '';
  const addressLocality = cityZipParts.length >= 2 ? cityZipParts.slice(1).join(' ') : cityZipParts[0] || 'Warszawa';

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "name": "Włoska piwnica",
        "url": "https://wloska-piwnica.pl",
        "description": "Ekskluzywny sklep z włoskimi winami z beczki, oliwą i włoskimi przekąskami."
      },
      {
        "@type": ["Store", "LocalBusiness", "Winery"],
        "name": "Włoska Piwnica",
        "description": "Sklep z włoskimi winami z beczki, oliwą i delikatesami z Apulii. Prawdziwy smak Włoch na Bemowie w Warszawie.",
        "url": "https://wloska-piwnica.pl",
        "telephone": settings.phone,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": streetAddress,
          "addressLocality": addressLocality,
          "addressRegion": "Mazowieckie",
          "postalCode": postalCode,
          "addressCountry": "PL"
        },
        "image": "https://wloska-piwnica.pl/og-image.jpg",
        "logo": "https://wloska-piwnica.pl/favicon.png",
        "paymentAccepted": "Cash, Credit Card, BLIK",
        "currenciesAccepted": "PLN",
        "sameAs": [
          settings.facebook_link !== '#' ? settings.facebook_link : '',
          settings.instagram_link !== '#' ? settings.instagram_link : ''
        ].filter(Boolean),
        "priceRange": "$$",
        "openingHoursSpecification": [
          { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "10:00", "closes": "19:00" },
          { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "10:00", "closes": "16:00" }
        ]
      }
    ]
  };

  return (
    <>
      <SEO 
        title="Włoska Piwnica — włoskie wina z beczki, oliwa i delikatesy | Bemowo, Warszawa"
        description="Sklep Włoska Piwnica na Bemowie — naturalne włoskie wina z beczki, oliwa extra vergine Galantino, tradycyjne przekąski z Apulii. Sklep stacjonarny Warszawa + wysyłka w całej Polsce."
        canonicalUrl="/"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen">
        <Header onCartClick={() => setIsCartOpen(true)} />

        <main>
          <Hero />

          <Suspense fallback={<div className="h-32 w-full animate-pulse bg-muted/20" />}>
            <TrustBadges />
            <CategoriesSection />

            {/* Featured Products - shows only a few with "Show more" button */}
            <FeaturedProducts />

            <TestimonialsSection />
            
            {/* Najnowsze z bloga */}
            <BlogLatestSection />

            {/* Burgundy stats accent strip */}
            <StatsStrip />

            <AboutSection />

            {/* Gallery */}
            <GallerySection />
          </Suspense>
        </main>

        <Suspense fallback={null}>
          <Footer />

          <CartDrawer
            isOpen={isCartOpen}
            onClose={() => setIsCartOpen(false)}
          />
        </Suspense>
      </div>
    </>
  );
};

export default Index;

